import { ConfirmModal, Quiz } from "../../components";
import { useEffect, useState, useCallback } from "react";
import { aulaService } from "../../api";
import { useNavigate, useParams } from "react-router-dom";
import { TQuiz, TAnswer, TAttempt } from "../../types/Aula";
import { useForm } from "@mantine/form";
import { TForm } from "../../types/Aula/TForm";
import { Flex } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { TLesson } from "../../types/Aula/TCourse";
import { showNotification } from "@mantine/notifications";

export const QuizPage = ({ nextLesson }: { nextLesson: TLesson | null }) => {
  const { id, lessonId } = useParams();
  const isMobile = useMediaQuery("(max-width: 992px)");
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<TQuiz | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitValues, setSubmitValues] = useState<TAnswer[]>([]);
  const [lastAttemptId, setLastAttemptId] = useState<string | null>(null);
  const [pastAttempts, setPastAttempts] = useState<TAttempt[]>([]);

  const form = useForm<TForm>({ initialValues: {} });

  const getQuizByLessonId = useCallback(async (lessonId: string): Promise<TQuiz | null> => {
    try {
      const response = await aulaService.getQuiz(lessonId);
      response.data.category = "quiz";
      const quizData = response.data as TQuiz;

      quizData.questions = quizData.questions.map((question) => ({
        ...question,
        choices: question.randomize_choices ? [...question.choices].sort(() => Math.random() - 0.5) : question.choices,
      }));

      const initialValues: TForm = quizData.questions.reduce((acc, question) => {
        acc[question.id] = question.category === "multiple_choice" ? [] : "";
        return acc;
      }, {} as TForm);

      form.setValues(initialValues);
      setQuiz(quizData);
      return quizData;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [form]);

  const getAttempts = useCallback(async (quizId: string, enrollmentId: string) => {
    try {
      const response = await aulaService.getAttempts(quizId, enrollmentId);
      const lastAttempt = response.data.attempts[0];

      setPastAttempts(lastAttempt?.finished_at ? response.data.attempts : response.data.attempts.slice(1));
      setLastAttemptId(lastAttempt?.finished_at ? "" : lastAttempt?.id ?? "");
    } catch (error) {
      console.error(error);
      setLastAttemptId("");
    }
  }, []);

  const handleSubmit = async () => {
    if (!form.validate().hasErrors && id && quiz) {
      try {
        const res = await aulaService.submitAnswers(quiz.id, {
          attempt_id: lastAttemptId,
          enrollment_id: id,
          answers: submitValues,
        });

        showNotification({
          title: res.data.is_approved ? "Éxito" : "Error",
          message: res.data.is_approved ? "🎉 ¡Quiz aprobado!" : "🚨 Quiz no aprobado",
          color: res.data.is_approved ? "green" : "red",
        });

        if (res.data.is_approved) {
          navigate(nextLesson ? `/aula-virtual/${id}/content/${nextLesson.id}` : `/aula-virtual/${id}`, {
            state: { category: nextLesson?.category, lessonCompleted: true },
          });
        } else {
          setTimeout(() => navigate(0), 2000);
        }
      } catch (error: any) {
        showNotification({
          title: "Error",
          message: error.response?.data?.error || "Ocurrió un error",
          color: "red",
        });
        console.error(error);
      }
    }
  };

  const handleFormSubmit = (values: TAnswer[], lastAttemptId: string) => {
    setSubmitValues(values);
    setLastAttemptId(lastAttemptId);
    setConfirmModalOpen(true);
  };

  useEffect(() => {
    goToQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const goToQuiz = async () => {
    if (lessonId) {
      const quiz = await getQuizByLessonId(lessonId);
      if (quiz && id) {
        await getAttempts(quiz.id, id);
      }
    }
  };

  return (
    <Flex direction="column" gap="md" w={isMobile ? "100%" : "75%"} m="auto">
        <Quiz
          quiz={quiz}
          enrollmentId={id}
          form={form}
          attemptId={lastAttemptId}
          attempts={pastAttempts}
          setAttemptId={setLastAttemptId}
          handleSubmit={handleFormSubmit}
          lessonId={lessonId}
        />
        <ConfirmModal
          confirmModalOpen={confirmModalOpen}
          setConfirmModalOpen={setConfirmModalOpen}
          submitValues={submitValues}
          handleSubmit={handleSubmit}
          quiz={quiz}
        />
    </Flex>
  );
};
